import React, { useEffect, useMemo, useRef, useState } from "react"
import { NavLink } from "react-router-dom"
import { jwtDecode } from "jwt-decode"

const linkStyle = ({ isActive }) => ({
display: "block",
padding: "7px 10px",
borderRadius: "8px",
color: isActive ? "#0369a1" : "var(--ac-text)",
fontWeight: isActive ? "700" : "500",
fontSize: "13px",
textDecoration: "none",
background: isActive ? "linear-gradient(90deg,#e0f2fe 0%,#f0f9ff 100%)" : "transparent",
borderLeft: isActive ? "3px solid #0ea5e9" : "3px solid transparent",
transition: "background 140ms ease, color 140ms ease"
})

const sectionCardStyle = {
background: "#ffffff",
border: "1px solid #e2e8f0",
borderRadius: "12px",
overflow: "hidden",
boxShadow: "0 1px 3px rgba(15,23,42,0.07), 0 1px 2px rgba(15,23,42,0.04)",
marginBottom: "10px"
}

const sectionLabelStyle = (gradient) => ({
fontSize: "10.5px",
letterSpacing: "1.3px",
fontWeight: "700",
color: "#ffffff",
padding: "6px 12px 5px",
background: gradient,
marginBottom: "6px"
})

const sectionBodyStyle = {
padding: "4px 6px 6px"
}

const SECTION_GRADIENTS = {
dashboard: "linear-gradient(90deg,#0f172a 0%,#1e3a5f 100%)",
build:     "linear-gradient(90deg,#0c4a6e 0%,#0369a1 100%)",
operate:   "linear-gradient(90deg,#155e75 0%,#0891b2 100%)",
knowledge: "linear-gradient(90deg,#1d4ed8 0%,#2563eb 100%)",
monitoring:"linear-gradient(90deg,#134e4a 0%,#0f766e 100%)",
integrations:"linear-gradient(90deg,#3b0764 0%,#6d28d9 100%)"
}

export default function Sidebar(){
const fileInputRef = useRef(null)
const [avatar,setAvatar] = useState("")
const [initials,setInitials] = useState("U")

const avatarStorageKey = useMemo(()=>{
	const token = localStorage.getItem("token")
	if(!token){
		return "agentcolab-avatar-anon"
	}

	try{
		const decoded = jwtDecode(token)
		const identity = decoded?.sub || decoded?.email || decoded?.user_id || "anon"
		return `agentcolab-avatar-${identity}`
	}catch{
		return "agentcolab-avatar-anon"
	}
},[])

useEffect(()=>{
	const existing = localStorage.getItem(avatarStorageKey) || ""
	setAvatar(existing)
},[avatarStorageKey])

useEffect(()=>{
	const token = localStorage.getItem("token")
	if(!token){
		setInitials("U")
		return
	}

	try{
		const decoded = jwtDecode(token)
		const raw = String(decoded?.sub || decoded?.email || "").trim()
		const localPart = raw.includes("@") ? raw.split("@")[0] : raw
		const words = localPart
			.replace(/[._-]+/g, " ")
			.split(" ")
			.filter(Boolean)

		if(words.length === 0){
			setInitials("U")
			return
		}

		const value = (words[0][0] || "") + (words[1]?.[0] || "")
		setInitials(value.toUpperCase() || "U")
	}catch{
		setInitials("U")
	}
},[])

const toggleAccountPanel = ()=>{
	window.dispatchEvent(new CustomEvent("agentcolab:toggle-account-panel"))
}

const onSelectAvatar = (event)=>{
	const file = event.target.files?.[0]
	if(!file){
		return
	}

	const reader = new FileReader()
	reader.onload = ()=>{
		const encoded = String(reader.result || "")
		if(!encoded){
			return
		}
		setAvatar(encoded)
		localStorage.setItem(avatarStorageKey, encoded)
	}
	reader.readAsDataURL(file)
}

const removeAvatar = ()=>{
	setAvatar("")
	localStorage.removeItem(avatarStorageKey)
	if(fileInputRef.current){
		fileInputRef.current.value = ""
	}
}

return(

<div style={{width:"260px",padding:"20px 16px",borderRight:"1px solid var(--ac-border)",background:"var(--ac-bg-soft)",display:"flex",flexDirection:"column",minHeight:"100vh"}}>

<div style={{padding:"6px 6px 16px 6px",borderBottom:"1px solid var(--ac-border-soft)",marginBottom:"16px"}}>
<img
src="/branding/agentcolab-logo.svg"
alt="AgentColab"
style={{width:"210px",maxWidth:"100%",display:"block"}}
/>
</div>

<div style={sectionCardStyle}>
<div style={sectionLabelStyle(SECTION_GRADIENTS.dashboard)}>DASHBOARD</div>
<div style={sectionBodyStyle}>
<NavLink to="/app" style={linkStyle}>Home</NavLink>
</div>
</div>

<div style={sectionCardStyle}>
<div style={sectionLabelStyle(SECTION_GRADIENTS.build)}>BUILD</div>
<div style={sectionBodyStyle}>
<NavLink to="/create-agent" style={linkStyle}>Create Agent</NavLink>
<NavLink to="/agents" style={linkStyle}>Agent Library</NavLink>
</div>
</div>

<div style={sectionCardStyle}>
<div style={sectionLabelStyle(SECTION_GRADIENTS.operate)}>OPERATE</div>
<div style={sectionBodyStyle}>
<NavLink to="/run-agent" style={linkStyle}>Run Agent</NavLink>
<NavLink to="/collaboration" style={linkStyle}>Collaboration</NavLink>
</div>
</div>

<div style={sectionCardStyle}>
<div style={sectionLabelStyle(SECTION_GRADIENTS.knowledge)}>KNOWLEDGE & ACCESS</div>
<div style={sectionBodyStyle}>
<NavLink to="/skills" style={linkStyle}>Skills</NavLink>
<NavLink to="/credential-profiles" style={linkStyle}>Credential Profiles</NavLink>
<NavLink to="/credential-health" style={linkStyle}>Credential Health</NavLink>
</div>
</div>

<div style={sectionCardStyle}>
<div style={sectionLabelStyle(SECTION_GRADIENTS.monitoring)}>MONITORING</div>
<div style={sectionBodyStyle}>
<NavLink to="/metrics" style={linkStyle}>System Metrics</NavLink>
<NavLink to="/usage" style={linkStyle}>Usage</NavLink>
<NavLink to="/activity" style={linkStyle}>Activity</NavLink>
<NavLink to="/approvals" style={linkStyle}>Approvals</NavLink>
<NavLink to="/logs" style={linkStyle}>Logs</NavLink>
</div>
</div>

<div style={sectionCardStyle}>
<div style={sectionLabelStyle(SECTION_GRADIENTS.integrations)}>INTEGRATIONS</div>
<div style={sectionBodyStyle}>
<NavLink to="/integrations" style={linkStyle}>Channels</NavLink>
<NavLink to="/api-keys" style={linkStyle}>API Keys</NavLink>
</div>
</div>

<div style={{marginTop:"auto",paddingTop:"14px",borderTop:"1px solid var(--ac-border-soft)"}}>
	<input
		ref={fileInputRef}
		type="file"
		accept="image/*"
		onChange={onSelectAvatar}
		style={{display:"none"}}
	/>
	<div style={{display:"flex",alignItems:"center",gap:"10px"}}>
		<button
			onClick={toggleAccountPanel}
			title="Toggle account panel"
			style={{
				width:"52px",
				height:"52px",
				borderRadius:"999px",
				border:"2px solid #0ea5e9",
				background: avatar ? `center / cover no-repeat url(${avatar})` : "#e0f2fe",
				color:"#0369a1",
				fontWeight:700,
				cursor:"pointer"
			}}
		>
			{!avatar ? initials : ""}
		</button>
		<div>
			<div style={{fontSize:"12px",fontWeight:700,color:"var(--ac-text)"}}>Your Avatar</div>
			<div style={{display:"flex",gap:"6px",marginTop:"4px"}}>
				<button
					onClick={()=>fileInputRef.current?.click()}
					style={{fontSize:"11px",border:"1px solid var(--ac-border)",borderRadius:"8px",padding:"2px 6px",background:"#fff",cursor:"pointer",color:"var(--ac-text)"}}
				>
					{avatar ? "Change" : "Upload"}
				</button>
				{avatar && (
					<button
						onClick={removeAvatar}
						style={{fontSize:"11px",border:"1px solid var(--ac-danger-border)",borderRadius:"8px",padding:"2px 6px",background:"var(--ac-danger-bg)",color:"var(--ac-danger-text)",cursor:"pointer"}}
					>
						Remove
					</button>
				)}
			</div>
		</div>
	</div>
</div>

</div>

)

}
